// src/pages/About.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";
import ModalPortal from "../components/ModalPortal";
import Seo from "../components/seo";

/* =========================================================
   THEME TOKENS (tweak here to re-skin fast)
========================================================= */
const WRAP = "px-[1vw]";              // global side padding (~5%)
const MAXW = "max-w-10xl mx-auto";     // page max width
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " + BORDER_SOFT + " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Color ribbons & gradients */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%) 
`;

/* Panels (non-glass, textile-like) */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";
const SILK_PLUM =
  "bg-[linear-gradient(135deg,rgba(56,18,40,0.8),rgba(12,45,79,0.2))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(75%_55%_at_15%_0%,rgba(140,59,90,0.3),transparent_100%)] before:content-['']";

/* Accent lines */
const ACCENT_LINE = "bg-gradient-to-r from-[#39C3D4] via-[#0FA7C0] to-[#8C3B5A]";

/* =========================================================
   SEO helpers
========================================================= */
const pageUrl = "https://tarotfate.com/about";
const siteUrl = "https://tarotfate.com";
const ogImage = "https://tarotfate.com/icons/og-cover.jpg";

const metaDescription =
  "Discover the story and philosophy of TarotFate. We offer emotion-based tarot for mindful reflection—not fortune telling—with guidance on how to use the site, ways to get in touch, and options to support.";

 // Page-specific keywords (About)
const aboutKeywords = `
about tarotfate, emotion-based tarot, tarot philosophy, ethical tarot, how to use tarot,
creator story, site mission, mindful tarot, reflective tarot, contact tarotfate, donate tarotfate
`.replace(/\s+/g, " ").trim();


const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: "https://tarotfate.com/icons/logo.webp",
        width: 512,
        height: 512
      }
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en"
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "About TarotFate | Emotion-Based Tarot & Our Philosophy",
      description: metaDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${pageUrl}#image` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
      inLanguage: "en"
    },
    {
      "@type": "ImageObject",
      "@id": `${pageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover"
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "About", item: pageUrl }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Is TarotFate predictive?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No. TarotFate emphasizes reflection, not fortune telling. Cards mirror your present so you can choose grounded next steps."
          }
        },
        {
          "@type": "Question",
          name: "Which spread should I choose?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "1 card = quick pulse; 3 cards = past–present–future; 5 cards = deeper map covering root, shadow, shift, strength, next step."
          }
        },
        {
          "@type": "Question",
          name: "Do I need to type a whisper or question?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "For “Other,” a short description is required. Otherwise it’s optional but helps personalize the reading."
          }
        },
        {
          "@type": "Question",
          name: "Do you store my data or readings?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No accounts or saved readings. Donations are handled securely by PayPal."
          }
        },
        {
          "@type": "Question",
          name: "Can I explore card meanings here?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes, Major Arcana and the suits Wands, Cups, Swords, Pentacles include upright & reversed meanings."
          }
        }
      ]
    }
  ]
};

export default function About() {
  /* =========================================
     Existing popup & donate plumbing (kept)
  ========================================= */
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showDonatePopup, setShowDonatePopup] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [contactMounted, setContactMounted] = useState(false);
  const [donateMounted, setDonateMounted] = useState(false);
  const [formState, handleSubmit] = useForm("xvgqwqll");

  const donationAmounts = [5, 10, 20, 50, 100];

  const [clickOrigin, setClickOrigin] = useState({
    x: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
    y: typeof window !== "undefined" ? window.innerHeight / 2 : 0
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showContactPopup) {
          setContactMounted(false);
          setTimeout(() => setShowContactPopup(false), 180);
        }
        if (showDonatePopup) {
          setDonateMounted(false);
          setTimeout(() => setShowDonatePopup(false), 180);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showContactPopup, showDonatePopup]);

  const recordClickCenter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setClickOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  };

  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      {/* SEO (Helmet replaced by Seo component) */}
     <Seo
  title="About TarotFate | Emotion-Based Tarot & Our Philosophy"
  description={metaDescription}
  url={pageUrl}
  image={ogImage}
  schema={schemaGraph}
  keywords={aboutKeywords}
/>


      {/* Ambient auras (no dots or noise) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* =========================
          HERO
      ========================= */}
      <section className={`pt-6 md:pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          About TarotFate
        </h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100 text-gl drop-shadow-[0_0_0px_rgba(203,183,234,0.8)]">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`mt-2 ${TEXT} text-base md:text-[18px]`}>
          A sanctuary for emotion-guided tarot. Reflection, not prediction.
        </p>  
      </section>

      {/* =========================
          ABOUT PANEL (silk, non-glass)
      ========================= */}
      <section className={`mt-8 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-2 sm:p-6 md:p-6 opatiy`}>
          <div className={`relative z-[1] ${TEXT} text-[17px] md:text-[18px] leading-[1.8]`}>
            <p>
              <strong className="text-white">Whispers of the Mystic Tarot Cards</strong>{" "}
              is a space born for those quiet moments in between everything. The
              times when emotions feel a little too loud, the world feels too fast,
              and one is not quite sure what the heart is trying to say.
            </p>
            <p className="mt-5">
              Think of this space like a soft pause button. It’s not about
              predicting the future or chasing cosmic answers. You won’t find
              crystal balls or dramatic prophecies here. Instead, this is a gentle
              corner where feelings are welcomed and clarity can come through
              quiet reflection.
            </p>
            <p className="mt-5">
              Maybe you’re facing a decision. Maybe something just feels off and
              you can’t explain it. Or maybe you’re simply curious and that’s
              completely okay too. Whatever brought you here, the mystical tarot
              cards are here to meet you as you are, whether you're feeling lost,
              hopeful, overwhelmed, or just up late with your thoughts.
            </p>
            <p className="mt-5">
              You don’t have to believe in magic to use this site. You just have
              to believe that your feelings matter, and that it’s okay to pause
              and ask yourself, <em>“What now?”</em>
            </p>
            <p className="mt-5">
              Bring tangled thoughts. Bring silence. Bring wonder. This space
              exists to honor what is felt.{" "}
              <strong className="text-white">One emotion at a time</strong>.
            </p>

            {/* Manifesto block */}
            <div className="mt-8 p-4 rounded-lg bg-white/5 border border-white/10">
              <h2 className="text-lg font-semibold text-white mb-3">
                At TarotFate, we believe tarot belongs to everyone.
              </h2>
              <p className="mt-3">
                Too often, people encounter card readings that charge high fees for
                generic, recycled answers. “Readers” who exploit vulnerability such as
                heartbreak, financial stress, or anxiety for profit. Gatekeepers who act
                as if tarot wisdom is a secret only they can provide.
              </p>
              <p className="mt-4">
                We reject this. Tarot is not about exploitation. It is not about
                creating dependency. It is not about keeping knowledge hidden.
              </p>
              <p className="mt-4">
                Tarot is about reflection, empowerment, and self-discovery. It is
                a tool to help you see your path more clearly. Not a business
                designed to prey on your pain.
              </p>
              <p className="mt-4">
                That’s why everything on TarotFate is free, open, and detailed.
                From the complete 78 card meanings to spreads, emotions, and
                rituals. You can access it all without walls, without tricks,
                without fear.
              </p>
              <p className="mt-4">
                TarotFate exists because your journey deserves to be guided by
                truth and insight, not manipulation.
              </p>
            </div>
          </div>
          {/* top edge gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* =========================
          HOW TO USE (dense + elegant)
      ========================= */}
      <section className={`mt-4 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} p-6 sm:p-8 md:p-10 bg-[linear-gradient(135deg,rgba(18,44,78,0.88),rgba(9,27,46,0.4),rgba(130,30,60,0.5))]`}>
          <div className="text-center">
            <div className="inline-flex items-center gap-3 uppercase tracking-[0.22em] text-base sm:text-base font-medium text-white/85">
              <span className="select-none"></span>
              <span>How to Use TarotFate</span>
              <span className="select-none"></span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Col 1 */}
            <div className="space-y-4">
              <StepCard step="1" title="Set Intention">
                Name your emotion or pick <em>Other</em> and write one clear sentence about what’s truly on your mind.
              </StepCard>
              <StepCard step="2" title="Choose a Spread">
                <b>1 card</b> quick pulse. <b>3 cards</b> past·present·future. <b>5 cards</b> root·shadow·shift·strength·step.
              </StepCard>
            </div>

            {/* Col 2 */}
            <div className="space-y-4">
              <StepCard step="3" title="Read Both Aspects">
                Consider <em>upright</em> and <em>reversed</em>. Pick the phrasing that accurately fits your reality.
              </StepCard>
              <StepCard step="4" title="Synthesize">
                Write one line: <em>“The message for me is…”</em> Then decide a single, gentle action you can take next.
              </StepCard>
            </div>

            {/* Col 3 */}
            <div className="space-y-4">
              <StepCard step="5" title="Revisit Mindfully">
                If you still feel unclear, step away and return later. Let the message settle before seeking again.
              </StepCard>
              <div className="rounded-xl border border-white/1 bg-white/5 p-4">
                <p className="uppercase tracking-widest text-[11px] text-white/80">Explore</p>
                <h3 className="mt-1 text-white font-semibold">Card Meanings</h3>
                <p className={`${TEXT} text-[15.5px] mt-1`}>
                  Deepen your reading with the Major & Minor Arcana.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/tarot/major-arcana-tarot-suit-cards-meaning" className="px-3 py-1 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 text-sm">✦ Major Arcana</Link>
                  <Link to="/tarot/wands-tarot-suit-cards-meaning" className="px-3 py-1 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 text-sm">✦ Wands</Link>
                  <Link to="/tarot/cups-tarot-suit-cards-meaning" className="px-3 py-1 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 text-sm">✦ Cups</Link>
                  <Link to="/tarot/swords-tarot-suit-cards-meaning" className="px-3 py-1 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 text-sm">✦ Swords</Link>
                  <Link to="/tarot/pentacles-tarot-suit-cards-meaning" className="px-3 py-1 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 text-sm">✦ Pentacles</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/40 bg-white/10 hover:bg-white/20 transition"
            >
              Start a Reading <span aria-hidden>↗</span>
            </Link>
          </div>
        </div>
      </section>

      {/* =========================
          CREATOR’S WHISPER
      ========================= */}
      <section
        aria-labelledby="creators-whisper"
        className={`relative mt-4 ${WRAP} ${MAXW}`}
      >
        {/* Ambient ornaments */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-10 -left-6 h-40 w-40 rounded-full blur-[90px] opacity-30
                     [background:radial-gradient(60%_60%_at_50%_50%,rgba(130,30,60,0.45),transparent)]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 rounded-full blur-[100px] opacity-25
                     [background:radial-gradient(60%_60%_at_50%_50%,rgba(3,121,155,0.45),transparent)]"
        />

        <div className={`${PANEL_BASE} ${SILK_PLUM} p-6 sm:p-8 md:p-10 relative overflow-hidden`}>
          {/* Section label */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 uppercase tracking-[0.22em] text-sm sm:text-base text-white/85">
              <span aria-hidden className="select-none"></span>
              <h2 id="creators-whisper" className="text-base sm:text-base font-medium">
                Creator’s Whisper
              </h2>
              <span aria-hidden className="select-none"></span>
            </div>
          </div>

          {/* Aphorisms + center sigil/divider */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-start md:items-center gap-6 md:gap-8">
            {/* Left column */}
            <div className={`${TEXT} text-sm sm:text-base leading-relaxed md:text-left`}>
              <p className="flex gap-2">
                <span aria-hidden>💫</span>
                <span>Align your steps with your purpose, the stars notice.</span>
              </p>
              <p className="mt-2 flex gap-2">
                <span aria-hidden>🌙</span>
                <span>What is steady and sacred gathers its own calm gravity.</span>
              </p>
            </div>

            {/* Center crest + line */}
            <div className="hidden md:flex flex-col items-center justify-center gap-3 px-3">
              <span className={`h-16 w-px ${ACCENT_LINE}`} />
              <span aria-hidden className="text-base">✦</span>
              <span className={`h-16 w-px ${ACCENT_LINE}`} />
            </div>

            {/* Right column */}
            <div className={`${TEXT} text-sm sm:text-base leading-relaxed md:text-left`}>
              <p className="flex gap-2">
                <span aria-hidden>🔮</span>
                <span>The mystic whispers when the soul listens and acts.</span>
              </p>
              <p className="mt-2 flex gap-2">
                <span aria-hidden>✨</span>
                <span>You are not alone. Let a small light guide the next right thing.</span>
              </p>
            </div>
          </div>

          {/* Card callout */}
          <div className="mt-8 text-center relative z-[1]">
            <p className="italic text-sm sm:text-base text-white/50">
              creator's guiding card: <span className="text-white/50 font-semibold">Ten of Swords</span> — endings that become awakenings.
            </p>

            <Link
              to="/tarot-card/ten-of-swords-tarot-card-reading-meaning"
              className="group inline-block mt-4 focus:outline-none focus:ring-2 focus:ring-white/60 rounded-lg"
            >
              <figure className="inline-flex flex-col items-center">
                <img
                  src="/card/ten-of-swords-tarot-card-reading-meaning.webp"
                  alt="Ten of Swords tarot card"
                  className="w-[108px] sm:w-[116px] md:w-[124px] rounded-lg shadow-[0_16px_48px_rgba(0,0,0,0.45)]
                             transition-transform duration-1 group-hover:-translate-y-0.5"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div className="mt-2 text-xs sm:text-sm text-white/50">
                  - rising from the ashes</div>
                <figcaption className="mt-2 text-xs sm:text-sm text-white/80">
                  See the full meaning ↗
                </figcaption>
              </figure>
            </Link>
          </div>

          {/* Bottom hairline */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-px opacity-70
                       bg-gradient-to-r from-transparent via-white/35 to-transparent"
          />
        </div>
      </section>

      {/* =========================
          CTA BUTTONS
      ========================= */}
      <section className={`mt-10 ${WRAP} ${MAXW}`}>
        <div className="flex justify-center items-center gap-6 sm:gap-8">
          <button
            onClick={(e) => {
              recordClickCenter(e);
              setShowContactPopup(true);
              requestAnimationFrame(() => setContactMounted(true));
            }}
            className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
          >
            📨 Contact
          </button>
          <button
            onClick={(e) => {
              recordClickCenter(e);
              setShowDonatePopup(true);
              requestAnimationFrame(() => setDonateMounted(true));
            }}
            className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
          >
            💖 Donate
          </button>
        </div>
      </section>

      {/* =========================
          FAQ — compact, left text + right “+”
      ========================= */}
      <section
        id="faq"
        className={`mt-12 mb-16 ${WRAP} ${MAXW}`}
      >
        <div className="text-center">
          <h2 className="text-base sm:text-lg font-medium">Frequently Asked Questions</h2>
          <div className="mt-2 flex justify-center">
          </div>
        </div>

        <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
          {[
            {
              q: "Is TarotFate predictive?",
              a:
                "No. TarotFate emphasizes reflection, not fortune telling. A reading mirrors what you’re navigating so you can choose grounded next steps."
            },
            {
              q: "Which spread should I choose?",
              a:
                "Choose 1 card for a quick pulse, 3 cards for past–present–future, and 5 cards for deeper mapping (root, shadow, shift, strength, next step)."
            },
            {
              q: "Do I need to type a whisper or question?",
              a:
                "For “Other,” a short sentence is required; otherwise it’s optional but helps the reading speak to your specific context."
            },
            {
              q: "Do you store my data or readings?",
              a:
                "No accounts and no saved readings. If you donate, PayPal securely handles payment."
            },
            {
              q: "Can I explore card meanings here?",
              a:
                "Yes — Major Arcana plus Wands, Cups, Swords, Pentacles with upright & reversed meanings for love, career, and guidance."
            }
          ].map(({ q, a }, i) => (
            <details key={i} className="group">
              <summary
                className="
                  cursor-pointer list-none px-4 md:px-6 py-3
                  text-sm sm:text-sm text-white/95 font-medium
                  flex items-start justify-between gap-6
                  after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                  group-open:after:content-['–']
                "
              >
                <span className="pr-2">{q}</span>
              </summary>
              <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* =========================
          POPUPS (unchanged)
      ========================= */}
      {showContactPopup && (
        <ModalPortal>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-[100]"
            onClick={() => {
              setContactMounted(false);
              setTimeout(() => setShowContactPopup(false), 180);
            }}
          >
            <div
              className={`bg-white/90 backdrop-blur-sm  p-6 rounded-lg w-full max-w-md shadow-lg relative transition duration-1 ease-out ${
                contactMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              style={{ transformOrigin: `${clickOrigin.x}px ${clickOrigin.y}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <Helmet>
                <script src="https://www.google.com/recaptcha/api.js" async defer></script>
              </Helmet>

              {formState.succeeded ? (
                <p className="text-green-600 text-center">Thank you! Your message has been sent.</p>
              ) : (
                <form onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    required
                    className="w-full mb-2 p-2 border border-gray-300 rounded"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email *"
                    required
                    pattern="^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
                    className="w-full mb-2 p-2 border border-gray-300 rounded"
                  />
                  <ValidationError prefix="Email" field="email" errors={formState.errors} />
                  <textarea
                    name="message"
                    placeholder="Your Message *"
                    required
                    className="w-full mb-2 p-2 border border-gray-300 rounded h-24"
                  />
                  <ValidationError prefix="Message" field="message" errors={formState.errors} />
                  <div className="g-recaptcha mb-3" data-sitekey="6LfViJsrAAAAAL98z_NLZIl4kEpH5plzrryGL-tv" />
                  <button
                    type="submit"
                    disabled={formState.submitting}
                    className="w-full py-2 bg-[#14656a] text-white rounded hover:text-white hover:bg-[#0f3d3f] transition"
                  >
                    Send Message
                  </button>
                </form>
              )}

              <button
                onClick={() => {
                  setContactMounted(false);
                  setTimeout(() => setShowContactPopup(false), 180);
                }}
                className="mt-4 text-sm text-gray-500 hover:underline block mx-auto"
              >
                Close
              </button>
            </div>
          </div>
        </ModalPortal>
      )}

      {showDonatePopup && (
        <ModalPortal>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-50"
            onClick={() => {
              setDonateMounted(false);
              setTimeout(() => setShowDonatePopup(false), 180);
            }}
          >
            <div
              className={`bg-[white]/85 backdrop-blur-sm p-6 rounded-lg w-full max-w-sm shadow-lg text-center transition duration-1 ease-out ${
                donateMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              style={{ transformOrigin: `${clickOrigin.x}px ${clickOrigin.y}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="mb-4 text-sm text-[#021868]">
                If this space offered you even a moment of clarity or calm, consider a small gift to help keep the light glowing for others who may need it too.
              </p>

              <div className="mt-4 flex flex-col items-center">
                <button
                  onClick={() => setShowDonation(!showDonation)}
                  className="bg-white/80 text-[#021868] border border-[#021868] px-5 py-2 rounded-full shadow-lg hover:text-white hover:bg-[#021868] transition"
                >
                  💖 Donate via PayPal
                </button>
                {showDonation && (
                  <DonateOptions
                    donationAmounts={donationAmounts}
                    customAmount={customAmount}
                    setCustomAmount={setCustomAmount}
                  />
                )}
              </div>

              <button
                onClick={() => {
                  setDonateMounted(false);
                  setTimeout(() => setShowDonatePopup(false), 180);
                }}
                className="mt-4 text-sm text-gray-500 hover:underline block mx-auto"
              >
                Close
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

/* =========================================================
   Subcomponents
========================================================= */
function StepCard({ step, title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <p className="uppercase tracking-widest text-[11px] text-white/80">Step {step}</p>
      <h3 className="mt-1 text-white font-semibold">{title}</h3>
      <p className="text-white/90 text-[15.5px] mt-1">{children}</p>
    </div>
  );
}

function DonateOptions({ donationAmounts, customAmount, setCustomAmount }) {
  return (
    <div className="mt-4 w-full flex flex-col items-center space-y-3">
      <div className="flex flex-wrap justify-center gap-3">
        {donationAmounts.map((amount) => (
          <a
            key={amount}
            href={`https://www.paypal.me/tarotfatecom/${amount}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-[#021868] text-[#021868] px-4 py-2 rounded-xl text-sm hover:text-white hover:bg-[#021868] transition"
          >
            ${amount}
          </a>
        ))}
      </div>
      <div className="text-center mt-2">
        <p className="text-xs text-[#021868] mb-1">Or enter your own amount (max $1,000)</p>
        <input
          type="text"
          inputMode="decimal"
          value={customAmount}
          onChange={(e) => {
            const v = e.target.value.trim();
            const ok = /^(\d+(\.\d{0,2})?)?$/.test(v);
            if (ok && parseFloat(v || "0") <= 1000) setCustomAmount(v);
          }}
          className="border border-[#c3b0e7] rounded px-3 py-1 w-32 text-center text-[#5e4b8b]"
          placeholder="Other ($)"
        />
        {customAmount && parseFloat(customAmount) > 0 && (
          <div className="mt-2">
            <a
              href={`https://www.paypal.me/tarotfatecom/${customAmount}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#021868] px-4 py-1.5 rounded-full shadow-lg hover:text-white hover:bg-[#021868] transition text-sm"
            >
              Donate ${customAmount}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
